// src/hooks/useProviderSearch.ts
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store';
import {
  searchRequest,
  setSearchContext,
  clearSearchResults,
  clearSelectedRows,
  setExternalSearchType
} from '../redux/slices/providerSearchSlice';
import { fetchVendorById } from '../redux/slices/providerSearchSlice';
import type { ProviderSearchFormData } from '../types/providerSearch';

export const useProviderSearch = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    searchResults,
    isLoading,
    error,
    searchContext,
    selectedRows,
    externalSearchType
  } = useSelector((state: RootState) => state.providerSearch);

  const search = (
    data: Record<string, string | string[]>,
    searchType: 'local' | 'name' | 'type' | 'region' = 'local',
    currentSearchCategory: 'auto' | 'property' = 'auto'
  ) => {
    // Update externalSearchType if needed
    if (searchType !== externalSearchType) {
      dispatch(setExternalSearchType(searchType));
    }

    // Transform payload to match API spec requirements
    const transformedPayload: Record<string, any> = {
      currentSearchCategory,
      // Capitalize searchType: local -> Local, name -> Name, type -> Type, region -> Region
      searchType: searchType.charAt(0).toUpperCase() + searchType.slice(1),
    };

    // Transform field names to match API spec (all lowercase fields)
    Object.entries(data).forEach(([key, value]) => {
      if (!value) return; // Skip empty values

      switch (key) {
        case 'city':
          transformedPayload.city = value; // lowercase
          break;
        case 'state':
          transformedPayload.state = value; // lowercase for all search types
          break;
        case 'zip':
          transformedPayload.zip = value; // lowercase
          break;
        case 'vendorType':
          {
            // Keep vendorType as string (vendor type code like "legal", "mitigation", etc.)
            transformedPayload.vendorType = value;
            // Also convert to numeric providerVendorType if it's a number
            const stringValue = Array.isArray(value) ? value[0] : value;
            const numericValue = parseInt(stringValue || '', 10);
            if (!isNaN(numericValue)) {
              transformedPayload.providerVendorType = numericValue;
            }
            break;
          }
        case 'vendorSpecialty':
          // Map vendorSpecialty to vendorSpecialtyTypeCode for API
          // Convert array to comma-separated string, or use single value
          if (Array.isArray(value) && value.length > 0) {
            transformedPayload.vendorSpecialtyTypeCode = value.join(',');
          } else if (typeof value === 'string' && value) {
            transformedPayload.vendorSpecialtyTypeCode = value;
          }
          break;
        case 'approvalStatus':
          // Normalize approvalStatus to array form; API layer also safeguards
          if (Array.isArray(value) && value.length > 0) {
            transformedPayload.approvalStatus = value.map(String);
          } else if (typeof value === 'string' && value) {
            transformedPayload.approvalStatus = value
              .split(',')
              .map(v => v.trim())
              .filter(v => v.length > 0);
          }
          break;
        default:
          // Keep other fields as-is (vendorName, address, searchDistance, etc.)
          transformedPayload[key] = value;
      }
    });

    // Set search context based on search type and category
    if (currentSearchCategory === 'auto') {
      if (searchType === 'local' && data.zip) {
        dispatch(setSearchContext({
          userZip: (data.zip as string).replace('-', '').substring(0, 5)
        }));
      }
      dispatch(searchRequest(transformedPayload));
    } else if (currentSearchCategory === 'property') {
      // For property, always set userType for external searches
      const isExternal = searchType === 'local' || searchType === 'name' || searchType === 'region';
      if (isExternal) {
        transformedPayload.userType = 'privileged';
      }
      dispatch(searchRequest(transformedPayload));
    }
  };

  const getVendorById = (id: string) => {
    dispatch(fetchVendorById(id));
  };

  const clearSearch = () => {
    dispatch(clearSearchResults());
    dispatch(clearSelectedRows());
  };

  return {
    search,
    getVendorById,
    clearSearch,
    searchResults,
    isLoading,
    error,
    searchContext,
    selectedRows,
    externalSearchType
  };
};
